import React, {createContext, useContext, useState, ReactNode} from 'react';
import {Dialog, DialogTitle, DialogContent, DialogActions} from '@mui/material';
import {Button} from "../../component/common/Button";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

type ConfirmOptions = {
    title?: string;
    content?: string;
    confirmText?: string;
    cancelText?: string;
};

type DialogContextType = {
    confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider = ({children}: { children: ReactNode }) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmOptions>({});
    const [resolver, setResolver] = useState<(result: boolean) => void>();
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const { t } = useTranslation();

    const confirm = (opts: ConfirmOptions) => {
        setOptions(opts);
        setOpen(true);
        return new Promise<boolean>((resolve) => {
            setResolver(() => resolve);
        });
    };

    const handleClose = (result: boolean) => {
        setOpen(false);
        if (resolver) resolver(result);
    };

    return (
        <DialogContext.Provider value={{confirm}}>
            {children}
            <Dialog open={open} onClose={() => handleClose(false)} PaperProps={{sx: {p: 3, borderRadius: 4, minWidth: isMobile ? 180 : 280}}}
            >
                <DialogTitle sx={{ pb: 1 }}>{options.title || t('common.dialog-default-title')}</DialogTitle>
                <DialogContent sx={{ pb: 2 }}>{options.content || t('common.dialog-confirm-title')}</DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2 }}>
                    <Button variant="secondary" onClick={() => handleClose(false)} text={options.cancelText || t('common.dialog-confirm-no')}/>
                    <Button onClick={() => handleClose(true)} text={options.confirmText || t('common.dialog-confirm-yes')}/>
                </DialogActions>
            </Dialog>
        </DialogContext.Provider>
    );
};

export const useDialog = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};
